import { useEffect, useState, FormEvent } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  Building2, Users, PawPrint, Activity, Plus, ShieldCheck, LogOut, Pencil, KeyRound, MapPin,
  DollarSign, TrendingUp, CalendarClock, Sparkles, Eye, UserX, X, Ticket, AlertTriangle, Search, Megaphone, MessageCircle,
  Send, BarChart3, GraduationCap, Clock, LifeBuoy, Copy, CheckCircle2, UserPlus,
} from "lucide-react";
import {
  LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  BarChart, Bar,
} from "recharts";
import { useSuperAdminAuth } from "../../context/SuperAdminAuthContext";
import { apiSuperAdmin, mensajeError } from "../../api/client";
import { Spinner } from "../../components/ui/Spinner";
import { Modal } from "../../components/ui/Modal";
import { Badge } from "../../components/ui/Badge";
import { RelojFechaHora } from "../../components/ui/RelojFechaHora";
import { DomicilioCampos, DomicilioValores } from "../../components/ui/DomicilioCampos";
import { NegociosMapa } from "./NegociosMapa";

const domicilioVacio: DomicilioValores = { calle: "", numero: "", codigoPostal: "", colonia: "", ciudad: "", estado: "", pais: "México" };

interface SolicitudDemo {
  id: string;
  nombre: string;
  negocio: string;
  telefono?: string | null;
  email: string;
  mensaje?: string | null;
  atendida: boolean;
  createdAt: string;
}

interface Suscripcion {
  id: string;
  plan: "MENSUAL" | "ANUAL" | "DEMO";
  estado: "ACTIVA" | "DEMO" | "VENCIDA" | "SUSPENDIDA" | "CANCELADA";
  fechaInicio: string;
  fechaVencimiento: string;
  canceladaEnPeriodo: boolean;
}

interface Negocio {
  id: string;
  nombre: string;
  subdominio: string;
  tipoNegocio: string;
  estado: "ACTIVO" | "INACTIVO" | "SUSPENDIDO";
  calle?: string | null;
  numero?: string | null;
  codigoPostal?: string | null;
  colonia?: string | null;
  ciudad?: string | null;
  estadoDireccion?: string | null;
  pais?: string | null;
  direccion?: string | null;
  telefono?: string | null;
  email?: string | null;
  latitud?: number | null;
  longitud?: number | null;
  timbresIncluidosMes: number;
  timbresExtra: number;
  createdAt: string;
  admin: { id: string; nombre: string; email: string } | null;
  totalClientes: number;
  totalMascotas: number;
  mascotasEliminadas: number;
  usoAproximado: number;
  ultimaActividad: string;
  ultimoLoginEn: string | null;
  suscripcion: Suscripcion | null;
}

interface SolicitudAlta {
  id: string;
  nombreNegocio: string;
  subdominio: string;
  email: string;
  telefono?: string | null;
  plan: "MENSUAL" | "ANUAL";
  estado: "PENDIENTE" | "COMPLETADA" | "FALLIDA";
  mensajeError?: string | null;
  tenantId?: string | null;
  stripeSessionId?: string | null;
  correoEnviado?: boolean | null;
  correoDetalle?: string | null;
  whatsappEnviado?: boolean | null;
  whatsappDetalle?: string | null;
  createdAt: string;
}

interface Resumen {
  totalNegocios: number;
  totalClientes: number;
  totalMascotas: number;
  mascotasEliminadas: number;
  activos: number;
  suspendidos: number;
  inactivos: number;
}

interface ClienteNegocio {
  id: string;
  nombre: string;
  telefono?: string | null;
  email?: string | null;
  activo: boolean;
  totalMascotas: number;
  createdAt: string;
}

interface MascotaNegocio {
  id: string;
  nombre: string;
  especie: string;
  raza?: string | null;
  activo: boolean;
  clienteNombre: string;
  eliminadaDelNegocio: boolean;
  motivoBaja?: string | null;
  motivoBajaDetalle?: string | null;
  fechaBaja?: string | null;
  createdAt: string;
}

const ETIQUETAS_MOTIVO_BAJA: Record<string, string> = {
  FALLECIMIENTO: "Falleció",
  PETICION_CLIENTE: "Petición del cliente",
  YA_NO_ES_CLIENTE: "Ya no es cliente",
  OTRO: "Otro motivo",
};

interface DashboardFinanciero {
  mrrEstimado: number;
  totalRecaudadoMes: number;
  ingresosPorMes: { mes: string; total: number }[];
  suscripcionesPorEstado: Record<string, number>;
  suscripcionesPorPlan: Record<string, number>;
  proximosVencimientos: { tenantId: string; nombre: string; fechaVencimiento: string; plan: string }[];
}

const COLORES_ESTADO: Record<string, string> = {
  ACTIVA: "#0d9488",
  DEMO: "#8b5cf6",
  VENCIDA: "#f59e0b",
  SUSPENDIDA: "#ef4444",
  CANCELADA: "#94a3b8",
};

function estadoSuscripcionColor(estado?: string): "verde" | "morado" | "amarillo" | "rojo" | "gris" {
  if (estado === "ACTIVA") return "verde";
  if (estado === "DEMO") return "morado";
  if (estado === "VENCIDA") return "amarillo";
  if (estado === "SUSPENDIDA") return "rojo";
  return "gris";
}

const COLORES_TARJETA = {
  azul: "bg-blue-50 text-blue-600",
  teal: "bg-teal-50 text-teal-600",
  morado: "bg-violet-50 text-violet-600",
  ambar: "bg-amber-50 text-amber-600",
  gris: "bg-slate-100 text-slate-600",
} as const;

function Tarjeta({
  icono: Icono,
  titulo,
  valor,
  color = "gris",
}: {
  icono: any;
  titulo: string;
  valor: string | number;
  color?: keyof typeof COLORES_TARJETA;
}) {
  return (
    <div className="group bg-white rounded-2xl shadow-sm border border-slate-100 p-5 flex items-center gap-4 transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-105 ${COLORES_TARJETA[color]}`}>
        <Icono size={20} />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-slate-500 truncate">{titulo}</p>
        <p className="text-xl font-bold text-slate-800">{valor}</p>
      </div>
    </div>
  );
}

function estadoColor(estado: string) {
  if (estado === "ACTIVO") return "verde";
  if (estado === "SUSPENDIDO") return "amarillo";
  return "rojo";
}

// Le da al super admin una señal visual rápida de qué tan reciente fue el
// último inicio de sesión de CUALQUIER usuario del negocio, para detectar de
// un vistazo cuáles siguen usándose y cuáles llevan tiempo abandonados.
function ultimoLoginInfo(fecha: string | null): { texto: string; color: "verde" | "amarillo" | "rojo" | "gris" } {
  if (!fecha) return { texto: "Nunca ha iniciado sesión", color: "gris" };

  const dias = Math.floor((Date.now() - new Date(fecha).getTime()) / (1000 * 60 * 60 * 24));
  const fechaTexto = new Date(fecha).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });

  if (dias <= 0) return { texto: `Hoy · ${fechaTexto}`, color: "verde" };
  if (dias === 1) return { texto: `Ayer · ${fechaTexto}`, color: "verde" };
  if (dias <= 7) return { texto: `Hace ${dias} días · ${fechaTexto}`, color: "verde" };
  if (dias <= 30) return { texto: `Hace ${dias} días · ${fechaTexto}`, color: "amarillo" };
  return { texto: `Hace ${dias} días · ${fechaTexto}`, color: "rojo" };
}

const tiposNegocio = [
  { value: "CLINICA", label: "Clínica veterinaria" },
  { value: "PET_SHOP", label: "Pet shop" },
  { value: "ESTETICA", label: "Estética" },
  { value: "HOTEL", label: "Hotel" },
  { value: "GUARDERIA", label: "Guardería" },
  { value: "ENTRENAMIENTO", label: "Entrenamiento" },
  { value: "MIXTO", label: "Mixto / varios servicios" },
];

export default function SuperAdminDashboard() {
  const { superAdmin, cargando: cargandoAuth, logout } = useSuperAdminAuth();
  const [negocios, setNegocios] = useState<Negocio[]>([]);
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [cargando, setCargando] = useState(true);

  // Navegación por pestañas del panel — antes todo el panel era un solo
  // scroll larguísimo con todo apilado; ahora se organiza en secciones
  // claras, más fácil de recorrer y entender de un vistazo.
  const [tabActiva, setTabActiva] = useState<"resumen" | "negocios" | "mapa" | "solicitudes">("resumen");
  const [busquedaNegocio, setBusquedaNegocio] = useState("");
  const [whatsappNoLeidos, setWhatsappNoLeidos] = useState(0);
  const [financiero, setFinanciero] = useState<DashboardFinanciero | null>(null);
  const [solicitudesDemo, setSolicitudesDemo] = useState<SolicitudDemo[]>([]);

  const [negocioDemo, setNegocioDemo] = useState<Negocio | null>(null);
  const [diasDemo, setDiasDemo] = useState(30);
  const [aplicandoDemo, setAplicandoDemo] = useState(false);
  const [errorDemo, setErrorDemo] = useState<string | null>(null);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [form, setForm] = useState({
    nombreNegocio: "", subdominio: "", tipoNegocio: "MIXTO", telefono: "",
    nombreAdmin: "", email: "", password: "",
  });
  const [domicilio, setDomicilio] = useState<DomicilioValores>(domicilioVacio);
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState<string | null>(null);

  const [negocioEditando, setNegocioEditando] = useState<Negocio | null>(null);
  const [formEditar, setFormEditar] = useState({
    nombre: "", tipoNegocio: "MIXTO", telefono: "", email: "",
    adminNombre: "", adminEmail: "",
  });
  const [domicilioEditar, setDomicilioEditar] = useState<DomicilioValores>(domicilioVacio);
  const [guardandoEditar, setGuardandoEditar] = useState(false);
  const [errorEditar, setErrorEditar] = useState<string | null>(null);

  const [reseteando, setReseteando] = useState<string | null>(null);
  const [passwordGenerada, setPasswordGenerada] = useState<{ email: string; passwordTemporal: string } | null>(null);

  const [reenviando, setReenviando] = useState<string | null>(null);
  const [accesoReenviado, setAccesoReenviado] = useState<{
    email: string;
    telefono: string | null;
    passwordTemporal: string;
    correoEnviado: boolean;
    correoDetalle: string | null;
    whatsappEnviado: boolean;
    whatsappDetalle: string | null;
  } | null>(null);

  // Datos de acceso del negocio recién creado, para poder copiarlos y
  // pegarlos donde el super admin quiera (WhatsApp, correo, etc.) — el
  // negocio también recibe esto automáticamente por WhatsApp/correo, pero
  // esto le da al super admin la opción de mandarlo él mismo por otro canal.
  const [datosAccesoNuevoNegocio, setDatosAccesoNuevoNegocio] = useState<{
    nombreNegocio: string;
    subdominio: string;
    email: string;
    password: string;
    telefono: string | null;
    correoEnviado: boolean;
    correoDetalle: string | null;
    whatsappEnviado: boolean;
    whatsappDetalle: string | null;
  } | null>(null);
  const [copiadoAcceso, setCopiadoAcceso] = useState(false);

  // Agregar timbres extra a un negocio (mientras se define el modelo de
  // precios de facturación) — cada negocio tiene una cuota mensual incluida
  // (2 por defecto) y esta bolsa extra es la forma de venderle/regalarle más
  // sin esperar al siguiente mes.
  const [negocioTimbres, setNegocioTimbres] = useState<Negocio | null>(null);
  const [cantidadTimbres, setCantidadTimbres] = useState(5);
  const [agregandoTimbres, setAgregandoTimbres] = useState(false);
  const [errorTimbres, setErrorTimbres] = useState<string | null>(null);

  // Crear un administrador nuevo para un negocio — la red de seguridad para
  // cuando el negocio se quedó sin ningún administrador activo (por ejemplo,
  // si borraron por accidente al único que tenían y ya nadie puede entrar).
  // También sirve para agregarle un segundo administrador a cualquier negocio.
  const [negocioNuevoAdmin, setNegocioNuevoAdmin] = useState<Negocio | null>(null);
  const [formNuevoAdmin, setFormNuevoAdmin] = useState({ nombre: "", email: "", password: "" });
  const [creandoAdmin, setCreandoAdmin] = useState(false);
  const [errorNuevoAdmin, setErrorNuevoAdmin] = useState<string | null>(null);
  const [adminCreado, setAdminCreado] = useState<{
    nombreNegocio: string;
    email: string;
    password: string;
    correoEnviado: boolean;
    correoDetalle: string | null;
    whatsappEnviado: boolean;
    whatsappDetalle: string | null;
  } | null>(null);

  // Solicitudes de alta con pago (autoservicio "Ya quiero usarlo") — red de
  // seguridad para cuando el webhook de Stripe no llega (ver GUIA adjunta):
  // el negocio se puede recuperar aquí con "Reintentar" sin depender del
  // webhook, consultando directo el Checkout Session en Stripe.
  const [solicitudesAlta, setSolicitudesAlta] = useState<SolicitudAlta[]>([]);
  const [reintentando, setReintentando] = useState<string | null>(null);
  const [errorReintentar, setErrorReintentar] = useState<string | null>(null);

  // Detalle de un negocio: todos sus clientes y mascotas (incluidas las
  // mascotas ya dadas de baja del negocio, que el negocio ya no ve pero el
  // super admin sigue necesitando consultar).
  const [negocioDetalle, setNegocioDetalle] = useState<Negocio | null>(null);
  const [tabDetalle, setTabDetalle] = useState<"clientes" | "mascotas">("clientes");
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [detalleClientes, setDetalleClientes] = useState<ClienteNegocio[]>([]);
  const [detalleMascotas, setDetalleMascotas] = useState<MascotaNegocio[]>([]);
  const [errorDetalle, setErrorDetalle] = useState<string | null>(null);

  async function cargar() {
    setCargando(true);
    try {
      const [resNegocios, resFinanciero, resDemo, resSolicitudesAlta] = await Promise.all([
        apiSuperAdmin.get("/superadmin/negocios"),
        apiSuperAdmin.get("/superadmin/dashboard-financiero"),
        apiSuperAdmin.get("/demo"),
        apiSuperAdmin.get("/superadmin/solicitudes-alta"),
      ]);
      setNegocios(resNegocios.data.items);
      setResumen(resNegocios.data.resumen);
      setFinanciero(resFinanciero.data);
      setSolicitudesDemo(resDemo.data.items ?? []);
      setSolicitudesAlta(resSolicitudesAlta.data.items ?? []);
    } finally {
      setCargando(false);
    }
  }

  async function marcarSolicitudDemo(id: string, atendida: boolean) {
    setSolicitudesDemo((prev) => prev.map((s) => (s.id === id ? { ...s, atendida } : s)));
    try {
      await apiSuperAdmin.put(`/demo/${id}`, { atendida });
    } catch (err) {
      alert(mensajeError(err));
      cargar();
    }
  }

  useEffect(() => {
    if (superAdmin) cargar();
  }, [superAdmin]);

  // Contador de mensajes de WhatsApp sin leer, para el aviso junto al enlace
  // de la bandeja en el encabezado — se revisa cada rato sin necesidad de
  // entrar a la bandeja para enterarse de que llegó algo nuevo.
  useEffect(() => {
    if (!superAdmin) return;
    const revisar = () => {
      apiSuperAdmin
        .get("/superadmin/whatsapp/resumen")
        .then((res) => setWhatsappNoLeidos(res.data.noLeidos))
        .catch(() => {});
    };
    revisar();
    const id = setInterval(revisar, 15000);
    return () => clearInterval(id);
  }, [superAdmin]);

  function abrirDemo(n: Negocio) {
    setNegocioDemo(n);
    setDiasDemo(30);
    setErrorDemo(null);
  }

  function abrirTimbres(n: Negocio) {
    setNegocioTimbres(n);
    setCantidadTimbres(5);
    setErrorTimbres(null);
  }

  function abrirCrearAdmin(n: Negocio) {
    setNegocioNuevoAdmin(n);
    setFormNuevoAdmin({ nombre: "", email: "", password: "" });
    setErrorNuevoAdmin(null);
  }

  async function crearAdministrador(e: FormEvent) {
    e.preventDefault();
    if (!negocioNuevoAdmin) return;
    setCreandoAdmin(true);
    setErrorNuevoAdmin(null);
    try {
      const res = await apiSuperAdmin.post(`/superadmin/negocios/${negocioNuevoAdmin.id}/crear-administrador`, formNuevoAdmin);
      setNegocioNuevoAdmin(null);
      setAdminCreado({
        nombreNegocio: negocioNuevoAdmin.nombre,
        email: formNuevoAdmin.email,
        password: formNuevoAdmin.password,
        correoEnviado: !!res.data?.correoEnviado,
        correoDetalle: res.data?.correoDetalle ?? null,
        whatsappEnviado: !!res.data?.whatsappEnviado,
        whatsappDetalle: res.data?.whatsappDetalle ?? null,
      });
      cargar();
    } catch (err) {
      setErrorNuevoAdmin(mensajeError(err));
    } finally {
      setCreandoAdmin(false);
    }
  }

  async function reintentarAlta(s: SolicitudAlta) {
    if (!confirm(`¿Reintentar la creación del negocio "${s.nombreNegocio}"? Esto solo funciona si Stripe confirma que el pago ya se completó.`)) return;
    setReintentando(s.id);
    setErrorReintentar(null);
    try {
      await apiSuperAdmin.post(`/superadmin/solicitudes-alta/${s.id}/reintentar`);
      cargar();
    } catch (err) {
      setErrorReintentar(mensajeError(err));
    } finally {
      setReintentando(null);
    }
  }

  async function agregarTimbres(e: FormEvent) {
    e.preventDefault();
    if (!negocioTimbres) return;
    setAgregandoTimbres(true);
    setErrorTimbres(null);
    try {
      const res = await apiSuperAdmin.post(`/superadmin/negocios/${negocioTimbres.id}/timbres`, { cantidad: cantidadTimbres });
      setNegocios((prev) => prev.map((n) => (n.id === negocioTimbres.id ? { ...n, timbresExtra: res.data.timbresExtra } : n)));
      setNegocioTimbres(null);
    } catch (err) {
      setErrorTimbres(mensajeError(err));
    } finally {
      setAgregandoTimbres(false);
    }
  }

  async function abrirDetalle(n: Negocio) {
    setNegocioDetalle(n);
    setTabDetalle("clientes");
    setCargandoDetalle(true);
    setErrorDetalle(null);
    try {
      const [resClientes, resMascotas] = await Promise.all([
        apiSuperAdmin.get(`/superadmin/negocios/${n.id}/clientes`),
        apiSuperAdmin.get(`/superadmin/negocios/${n.id}/mascotas`),
      ]);
      setDetalleClientes(resClientes.data.items ?? []);
      setDetalleMascotas(resMascotas.data.items ?? []);
    } catch (err) {
      setErrorDetalle(mensajeError(err));
    } finally {
      setCargandoDetalle(false);
    }
  }

  async function aplicarDemo(e: FormEvent) {
    e.preventDefault();
    if (!negocioDemo) return;
    setAplicandoDemo(true);
    setErrorDemo(null);
    try {
      await apiSuperAdmin.post(`/superadmin/negocios/${negocioDemo.id}/aplicar-demo`, { dias: diasDemo });
      setNegocioDemo(null);
      cargar();
    } catch (err) {
      setErrorDemo(mensajeError(err));
    } finally {
      setAplicandoDemo(false);
    }
  }

  if (!cargandoAuth && !superAdmin) return <Navigate to="/superadmin/login" replace />;
  if (cargandoAuth) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;

  async function cambiarEstado(id: string, estado: string) {
    const anteriores = negocios;
    setNegocios((prev) => prev.map((n) => (n.id === id ? { ...n, estado: estado as any } : n)));
    try {
      await apiSuperAdmin.put(`/superadmin/negocios/${id}/estado`, { estado });
    } catch (err) {
      setNegocios(anteriores);
      alert(mensajeError(err));
    }
  }

  function abrirNuevo() {
    setForm({ nombreNegocio: "", subdominio: "", tipoNegocio: "MIXTO", telefono: "", nombreAdmin: "", email: "", password: "" });
    setDomicilio(domicilioVacio);
    setErrorForm(null);
    setModalAbierto(true);
  }

  async function crearNegocio(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setErrorForm(null);
    try {
      // El backend espera el estado del domicilio como `estadoDireccion`
      // (el nombre `estado` ya lo usa el negocio para ACTIVO/INACTIVO/SUSPENDIDO).
      const { estado: estadoDomicilio, ...restoDomicilio } = domicilio;
      const res = await apiSuperAdmin.post("/superadmin/negocios", { ...form, ...restoDomicilio, estadoDireccion: estadoDomicilio });
      setModalAbierto(false);
      // El backend ya intentó mandar los datos de acceso por WhatsApp/correo
      // y regresa si de verdad se lograron mandar (no se asume éxito) — se
      // guarda aquí junto con la contraseña que el super admin acaba de
      // escribir, para mostrar el resultado real y, de cualquier forma,
      // dejar la ventana de copiar y pegar como respaldo.
      setDatosAccesoNuevoNegocio({
        nombreNegocio: form.nombreNegocio,
        subdominio: form.subdominio,
        email: form.email,
        password: form.password,
        telefono: form.telefono || null,
        correoEnviado: !!res.data?.correoEnviado,
        correoDetalle: res.data?.correoDetalle ?? null,
        whatsappEnviado: !!res.data?.whatsappEnviado,
        whatsappDetalle: res.data?.whatsappDetalle ?? null,
      });
      cargar();
    } catch (err) {
      setErrorForm(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  function mensajeAccesoNuevoNegocio(): string {
    if (!datosAccesoNuevoNegocio) return "";
    const { subdominio, email, password } = datosAccesoNuevoNegocio;
    return `Bienvenido a Mascotas Vet, hemos creado un portal web especialmente para su negocio y podrá acceder con los siguientes datos:
Portal web: https://${subdominio}.mascotas.vet
Usuario: ${email}
Contraseña: ${password}

Cualquier duda que tengas por favor házmela saber, también puedes ver nuestros tutoriales aquí: https://mascotas.vet/tutoriales`;
  }

  function copiarDatosAccesoNuevoNegocio() {
    navigator.clipboard.writeText(mensajeAccesoNuevoNegocio()).then(() => {
      setCopiadoAcceso(true);
      setTimeout(() => setCopiadoAcceso(false), 2000);
    });
  }

  function abrirEditar(n: Negocio) {
    setNegocioEditando(n);
    setFormEditar({
      nombre: n.nombre,
      tipoNegocio: n.tipoNegocio,
      telefono: n.telefono ?? "",
      email: n.email ?? "",
      adminNombre: n.admin?.nombre ?? "",
      adminEmail: n.admin?.email ?? "",
    });
    setDomicilioEditar({
      calle: n.calle ?? "",
      numero: n.numero ?? "",
      codigoPostal: n.codigoPostal ?? "",
      colonia: n.colonia ?? "",
      ciudad: n.ciudad ?? "",
      estado: n.estadoDireccion ?? "",
      pais: n.pais ?? "México",
    });
    setErrorEditar(null);
  }

  async function guardarEdicion(e: FormEvent) {
    e.preventDefault();
    if (!negocioEditando) return;
    setGuardandoEditar(true);
    setErrorEditar(null);
    try {
      const { estado: estadoDomicilio, ...restoDomicilio } = domicilioEditar;
      await apiSuperAdmin.put(`/superadmin/negocios/${negocioEditando.id}`, {
        ...formEditar,
        ...restoDomicilio,
        estadoDireccion: estadoDomicilio,
      });
      setNegocioEditando(null);
      cargar();
    } catch (err) {
      setErrorEditar(mensajeError(err));
    } finally {
      setGuardandoEditar(false);
    }
  }

  async function restablecerPassword(n: Negocio) {
    if (!n.admin) {
      alert("Este negocio no tiene un usuario administrador.");
      return;
    }
    if (!confirm(`¿Restablecer la contraseña del administrador de "${n.nombre}" (${n.admin.email})?`)) return;
    setReseteando(n.id);
    try {
      const res = await apiSuperAdmin.post(`/superadmin/negocios/${n.id}/reset-password`);
      setPasswordGenerada(res.data);
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setReseteando(null);
    }
  }

  async function reenviarAcceso(n: Negocio) {
    if (!n.admin) {
      alert("Este negocio no tiene un usuario administrador.");
      return;
    }
    if (
      !confirm(
        `¿Reenviar los datos de acceso de "${n.nombre}" a ${n.admin.email}${n.telefono ? ` y por WhatsApp al ${n.telefono}` : ""}? Esto genera una contraseña nueva (la anterior deja de funcionar).`
      )
    )
      return;
    setReenviando(n.id);
    try {
      const res = await apiSuperAdmin.post(`/superadmin/negocios/${n.id}/reenviar-acceso`);
      setAccesoReenviado(res.data);
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setReenviando(null);
    }
  }

  const pendientesAlta = solicitudesAlta.filter((s) => s.estado === "PENDIENTE").length;
  const demosSinAtender = solicitudesDemo.filter((s) => !s.atendida).length;
  const totalPendientes = pendientesAlta + demosSinAtender;

  const negociosFiltrados = negocios.filter((n) => {
    const q = busquedaNegocio.trim().toLowerCase();
    if (!q) return true;
    return n.nombre.toLowerCase().includes(q) || n.subdominio.toLowerCase().includes(q);
  });

  const TABS: { id: typeof tabActiva; etiqueta: string; icono: any; contador?: number }[] = [
    { id: "resumen", etiqueta: "Resumen", icono: Activity },
    { id: "negocios", etiqueta: "Negocios", icono: Building2 },
    { id: "mapa", etiqueta: "Mapa", icono: MapPin },
    { id: "solicitudes", etiqueta: "Solicitudes", icono: AlertTriangle, contador: totalPendientes },
  ];

  return (
    <div className="fondo-marca-agua min-h-screen">
      <header className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-slate-900 to-blue-950 text-white">
        <div className="relative flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-2 font-bold">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/20 text-blue-300">
              <ShieldCheck size={18} />
            </span>
            Portal Super Admin
          </div>
          <div className="flex items-center gap-4">
            <Link to="/superadmin/whatsapp" className="flex items-center gap-1.5 text-sm text-slate-300 transition-colors hover:text-white">
              <MessageCircle size={16} /> WhatsApp
              {whatsappNoLeidos > 0 && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {whatsappNoLeidos}
                </span>
              )}
            </Link>
            <Link to="/superadmin/campanas" className="flex items-center gap-1.5 text-sm text-slate-300 transition-colors hover:text-white">
              <Megaphone size={16} /> Campañas
            </Link>
            <Link to="/superadmin/analitica" className="flex items-center gap-1.5 text-sm text-slate-300 transition-colors hover:text-white">
              <BarChart3 size={16} /> Analítica
            </Link>
            <Link to="/superadmin/tutoriales" className="flex items-center gap-1.5 text-sm text-slate-300 transition-colors hover:text-white">
              <GraduationCap size={16} /> Tutoriales
            </Link>
            <Link to="/superadmin/soporte" className="flex items-center gap-1.5 text-sm text-slate-300 transition-colors hover:text-white">
              <LifeBuoy size={16} /> Soporte
            </Link>
            <RelojFechaHora claro />
            <span className="hidden items-center gap-2 text-sm text-slate-300 sm:flex">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-700 text-xs font-semibold uppercase text-white">
                {superAdmin?.nombre?.[0] ?? "A"}
              </span>
              {superAdmin?.nombre}
            </span>
            <button onClick={logout} className="flex items-center gap-1 text-sm text-red-300 transition-colors hover:text-red-200">
              <LogOut size={16} /> Salir
            </button>
          </div>
        </div>

        {/* Navegación por pestañas */}
        <nav className="relative flex gap-1 overflow-x-auto px-4 sm:px-6">
          {TABS.map((t) => {
            const Icono = t.icono;
            const activa = tabActiva === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTabActiva(t.id)}
                className={`relative flex items-center gap-1.5 whitespace-nowrap rounded-t-lg px-3.5 py-2.5 text-sm font-medium transition-colors ${
                  activa ? "bg-slate-50 text-slate-800" : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icono size={15} />
                {t.etiqueta}
                {!!t.contador && (
                  <span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {t.contador}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto p-6">
        {cargando || !resumen ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : (
          <>
            {tabActiva === "resumen" && (
            <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Tarjeta icono={Building2} titulo="Negocios totales" valor={resumen.totalNegocios} color="azul" />
              <Tarjeta icono={Users} titulo="Clientes totales" valor={resumen.totalClientes} color="teal" />
              <Tarjeta icono={PawPrint} titulo="Mascotas totales" valor={resumen.totalMascotas} color="morado" />
              <Tarjeta icono={Activity} titulo="Activos / suspendidos / inactivos" valor={`${resumen.activos} / ${resumen.suspendidos} / ${resumen.inactivos}`} color="ambar" />
            </div>

            <h2 className="text-lg font-semibold text-slate-700 mb-4">Negocios, clientes y mascotas</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
              <div className="card p-5 lg:col-span-2">
                <h3 className="mb-4 text-sm font-semibold text-slate-700">Clientes y mascotas por negocio</h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[...negocios]
                        .sort((a, b) => b.totalMascotas - a.totalMascotas)
                        .slice(0, 8)
                        .map((n) => ({ nombre: n.nombre, Clientes: n.totalClientes, Mascotas: n.totalMascotas }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="nombre" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Clientes" fill="#0d9488" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Mascotas" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card p-5">
                <h3 className="mb-4 text-sm font-semibold text-slate-700">Mascotas: activas vs. dadas de baja</h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { estado: "Activas", cantidad: resumen.totalMascotas - resumen.mascotasEliminadas },
                          { estado: "Dadas de baja", cantidad: resumen.mascotasEliminadas },
                        ]}
                        dataKey="cantidad"
                        nameKey="estado"
                        innerRadius={40}
                        outerRadius={70}
                      >
                        <Cell fill="#0d9488" />
                        <Cell fill="#cbd5e1" />
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-2 text-center text-xs text-slate-400">
                  Las mascotas dadas de baja conservan su expediente completo — solo dejaron de aparecer en el panel de
                  su negocio.
                </p>
              </div>
            </div>

            {financiero && (
              <>
                <h2 className="text-lg font-semibold text-slate-700 mb-4">Finanzas y suscripciones</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <Tarjeta
                    icono={TrendingUp}
                    titulo="MRR estimado"
                    valor={`$${financiero.mrrEstimado.toLocaleString("es-MX")}`}
                    color="teal"
                  />
                  <Tarjeta
                    icono={DollarSign}
                    titulo="Recaudado este mes"
                    valor={`$${financiero.totalRecaudadoMes.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`}
                    color="azul"
                  />
                  <Tarjeta
                    icono={Sparkles}
                    titulo="Suscripciones activas"
                    valor={financiero.suscripcionesPorEstado.ACTIVA ?? 0}
                    color="morado"
                  />
                  <Tarjeta
                    icono={CalendarClock}
                    titulo="Vencen en 7 días"
                    valor={financiero.proximosVencimientos.length}
                    color="ambar"
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                  <div className="card p-5 lg:col-span-2">
                    <h3 className="mb-4 text-sm font-semibold text-slate-700">Ingresos por mes</h3>
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={financiero.ingresosPorMes}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v: number) => `$${v.toLocaleString("es-MX")}`} />
                          <Line type="monotone" dataKey="total" stroke="#0d9488" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="card p-5">
                    <h3 className="mb-4 text-sm font-semibold text-slate-700">Suscripciones por estado</h3>
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={Object.entries(financiero.suscripcionesPorEstado).map(([estado, cantidad]) => ({ estado, cantidad }))}
                            dataKey="cantidad"
                            nameKey="estado"
                            innerRadius={40}
                            outerRadius={70}
                          >
                            {Object.keys(financiero.suscripcionesPorEstado).map((estado) => (
                              <Cell key={estado} fill={COLORES_ESTADO[estado] ?? "#94a3b8"} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {financiero.proximosVencimientos.length > 0 && (
                  <div className="card p-5 mb-8">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <CalendarClock size={16} /> Próximos vencimientos (7 días)
                    </h3>
                    <ul className="divide-y divide-slate-50 text-sm">
                      {financiero.proximosVencimientos.map((v) => (
                        <li key={v.tenantId} className="flex items-center justify-between py-2">
                          <span className="text-slate-700">{v.nombre}</span>
                          <span className="text-slate-400 text-xs">
                            {v.plan} · vence el {new Date(v.fechaVencimiento).toLocaleDateString("es-MX")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            </>
            )}

            {tabActiva === "solicitudes" && (
            <>
            {solicitudesAlta.some((s) => s.estado === "PENDIENTE") && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
                    <AlertTriangle size={18} className="text-amber-500" /> Altas con pago pendientes de confirmar
                  </h2>
                  <span className="text-xs text-slate-400">
                    {solicitudesAlta.filter((s) => s.estado === "PENDIENTE").length} pendiente(s)
                  </span>
                </div>
                <p className="mb-3 text-xs text-slate-500">
                  Estas solicitudes ya iniciaron el pago en Stripe pero el negocio todavía no se ha creado — casi
                  siempre porque el webhook de Stripe no llegó a tiempo (o su URL está mal configurada). Si el
                  prospecto ya te confirmó que Stripe sí le cobró, dale "Reintentar": consulta directo con Stripe si
                  el pago se completó y, si es así, crea el negocio en ese momento.
                </p>
                {errorReintentar && <p className="mb-3 text-sm text-red-600">{errorReintentar}</p>}
                <div className="card overflow-x-auto mb-8">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                        <th className="px-4 py-3 font-medium">Fecha</th>
                        <th className="px-4 py-3 font-medium">Negocio</th>
                        <th className="px-4 py-3 font-medium">Correo</th>
                        <th className="px-4 py-3 font-medium">Plan</th>
                        <th className="px-4 py-3 font-medium">Estado</th>
                        <th className="px-4 py-3 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {solicitudesAlta
                        .filter((s) => s.estado === "PENDIENTE")
                        .map((s) => (
                          <tr key={s.id} className="border-b border-slate-50 last:border-0">
                            <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                              {new Date(s.createdAt).toLocaleString("es-MX")}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-700">
                              {s.nombreNegocio}
                              <p className="text-xs font-normal text-slate-400">{s.subdominio}.mascotas.vet</p>
                            </td>
                            <td className="px-4 py-3 text-slate-600">{s.email}</td>
                            <td className="px-4 py-3 text-slate-600">{s.plan}</td>
                            <td className="px-4 py-3">
                              <Badge color="amarillo">Pendiente</Badge>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                className="btn-primary !py-1 !text-xs"
                                disabled={reintentando === s.id}
                                onClick={() => reintentarAlta(s)}
                              >
                                {reintentando === s.id ? "Verificando..." : "Reintentar"}
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {solicitudesAlta.some((s) => s.estado === "COMPLETADA" || s.estado === "FALLIDA") && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-700">Altas con pago — historial reciente</h2>
                </div>
                <p className="mb-3 text-xs text-slate-500">
                  Cuando el negocio se crea automáticamente (pago confirmado por Stripe), se intenta mandar de inmediato
                  el correo y el WhatsApp con sus datos de acceso. Aquí se ve si de verdad se mandaron o no — si algo
                  falló, el botón "Reenviar datos de acceso" en la pestaña "Negocios" es el respaldo.
                </p>
                <div className="card overflow-x-auto mb-8">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                        <th className="px-4 py-3 font-medium">Fecha</th>
                        <th className="px-4 py-3 font-medium">Negocio</th>
                        <th className="px-4 py-3 font-medium">Estado</th>
                        <th className="px-4 py-3 font-medium">Correo</th>
                        <th className="px-4 py-3 font-medium">WhatsApp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {solicitudesAlta
                        .filter((s) => s.estado === "COMPLETADA" || s.estado === "FALLIDA")
                        .slice(0, 15)
                        .map((s) => (
                          <tr key={s.id} className="border-b border-slate-50 last:border-0">
                            <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                              {new Date(s.createdAt).toLocaleString("es-MX")}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-700">
                              {s.nombreNegocio}
                              <p className="text-xs font-normal text-slate-400">{s.subdominio}.mascotas.vet · {s.email}</p>
                            </td>
                            <td className="px-4 py-3">
                              {s.estado === "COMPLETADA" ? (
                                <Badge color="verde">Completada</Badge>
                              ) : (
                                <span title={s.mensajeError ?? undefined}>
                                  <Badge color="rojo">Fallida</Badge>
                                </span>
                              )}
                            </td>
                            {s.estado === "FALLIDA" ? (
                              <td className="px-4 py-3 text-xs text-slate-400" colSpan={2}>
                                {s.mensajeError || "—"}
                              </td>
                            ) : (
                              <>
                                <td className="px-4 py-3">
                                  {s.correoEnviado ? (
                                    <Badge color="verde">Enviado</Badge>
                                  ) : (
                                    <span title={s.correoDetalle ?? undefined}>
                                      <Badge color="rojo">Falló</Badge>
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {!s.telefono ? (
                                    <span className="text-xs text-slate-400">Sin teléfono</span>
                                  ) : s.whatsappEnviado ? (
                                    <Badge color="verde">Enviado</Badge>
                                  ) : (
                                    <span title={s.whatsappDetalle ?? undefined}>
                                      <Badge color="rojo">Falló</Badge>
                                    </span>
                                  )}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {solicitudesDemo.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-700">Solicitudes de demo (portal de inicio)</h2>
                  <span className="text-xs text-slate-400">
                    {solicitudesDemo.filter((s) => !s.atendida).length} sin atender
                  </span>
                </div>
                <div className="card overflow-x-auto mb-8">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                        <th className="px-4 py-3 font-medium">Fecha</th>
                        <th className="px-4 py-3 font-medium">Negocio</th>
                        <th className="px-4 py-3 font-medium">Contacto</th>
                        <th className="px-4 py-3 font-medium">Mensaje</th>
                        <th className="px-4 py-3 font-medium">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {solicitudesDemo.map((s) => (
                        <tr key={s.id} className="border-b border-slate-50 last:border-0">
                          <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                            {new Date(s.createdAt).toLocaleDateString("es-MX")}
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-700">{s.negocio}</td>
                          <td className="px-4 py-3 text-slate-600">
                            <p>{s.nombre}</p>
                            <p className="text-xs text-slate-400">
                              {s.email}
                              {s.telefono ? ` · ${s.telefono}` : ""}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-slate-500 max-w-xs">{s.mensaje || "—"}</td>
                          <td className="px-4 py-3">
                            <button
                              className="btn-secondary !py-1 !text-xs"
                              onClick={() => marcarSolicitudDemo(s.id, !s.atendida)}
                            >
                              {s.atendida ? "Atendida" : "Marcar como atendida"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {pendientesAlta === 0 && solicitudesDemo.length === 0 && (
              <div className="card p-10 text-center text-sm text-slate-400">
                No hay solicitudes de alta ni de demo pendientes por ahora.
              </div>
            )}
            </>
            )}

            {tabActiva === "negocios" && (
            <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-700">Negocios</h2>
              <button className="btn-primary" onClick={abrirNuevo}>
                <Plus size={16} /> Nuevo negocio
              </button>
            </div>

            <div className="relative mb-4 max-w-sm">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="input !pl-9"
                placeholder="Buscar por nombre o subdominio..."
                value={busquedaNegocio}
                onChange={(e) => setBusquedaNegocio(e.target.value)}
              />
            </div>

            <div className="card overflow-x-auto mb-8">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3 font-medium">Negocio</th>
                    <th className="px-4 py-3 font-medium">Administrador</th>
                    <th className="px-4 py-3 font-medium">Teléfono</th>
                    <th className="px-4 py-3 font-medium">Creado</th>
                    <th className="px-4 py-3 font-medium">Última conexión</th>
                    <th className="px-4 py-3 font-medium">Clientes</th>
                    <th className="px-4 py-3 font-medium">Mascotas</th>
                    <th className="px-4 py-3 font-medium">Uso aprox.</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                    <th className="px-4 py-3 font-medium">Suscripción</th>
                    <th className="px-4 py-3 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {negociosFiltrados.map((n) => (
                    <tr key={n.id} className="border-b border-slate-50 last:border-0 align-top">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-700">{n.nombre}</p>
                        <p className="text-slate-400 text-xs">{n.subdominio}.mascotas.vet</p>
                        {n.direccion && (
                          <p className="text-slate-400 text-xs flex items-center gap-1 mt-0.5">
                            <MapPin size={11} /> {n.direccion}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {n.admin ? (
                          <>
                            <p>{n.admin.nombre}</p>
                            <p className="text-slate-400 text-xs">{n.admin.email}</p>
                          </>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
                            <AlertTriangle size={12} /> Sin administrador activo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{n.telefono || "-"}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{new Date(n.createdAt).toLocaleDateString("es-MX")}</td>
                      <td className="px-4 py-3">
                        {(() => {
                          const info = ultimoLoginInfo(n.ultimoLoginEn);
                          return (
                            <span className="inline-flex items-center gap-1.5">
                              <Badge color={info.color}>
                                <Clock size={11} className="mr-1 inline" /> {info.texto}
                              </Badge>
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{n.totalClientes}</td>
                      <td className="px-4 py-3 text-slate-700">{n.totalMascotas}</td>
                      <td className="px-4 py-3 text-slate-700">{n.usoAproximado}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Badge color={estadoColor(n.estado)}>{n.estado}</Badge>
                          <select
                            className="input !w-auto !py-1 !text-xs"
                            value={n.estado}
                            onChange={(e) => cambiarEstado(n.id, e.target.value)}
                          >
                            <option value="ACTIVO">Activo</option>
                            <option value="INACTIVO">Inactivo</option>
                            <option value="SUSPENDIDO">Suspendido</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {n.suscripcion ? (
                          <>
                            <Badge color={estadoSuscripcionColor(n.suscripcion.estado)}>{n.suscripcion.estado}</Badge>
                            <p className="text-slate-400 text-xs mt-1">
                              {n.suscripcion.plan} · vence {new Date(n.suscripcion.fechaVencimiento).toLocaleDateString("es-MX")}
                            </p>
                          </>
                        ) : (
                          <span className="text-slate-400 text-xs">Sin suscripción</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button className="btn-ghost !px-2" title="Ver clientes y mascotas" onClick={() => abrirDetalle(n)}>
                            <Eye size={15} />
                          </button>
                          <button className="btn-ghost !px-2" title="Editar negocio" onClick={() => abrirEditar(n)}>
                            <Pencil size={15} />
                          </button>
                          <button
                            className="btn-ghost !px-2"
                            title="Restablecer contraseña del administrador"
                            disabled={reseteando === n.id}
                            onClick={() => restablecerPassword(n)}
                          >
                            <KeyRound size={15} />
                          </button>
                          <button
                            className="btn-ghost !px-2"
                            title="Reenviar datos de acceso por WhatsApp y correo"
                            disabled={reenviando === n.id}
                            onClick={() => reenviarAcceso(n)}
                          >
                            <Send size={15} />
                          </button>
                          <button
                            className={`btn-ghost !px-2 ${!n.admin ? "!text-red-600" : ""}`}
                            title={n.admin ? "Crear otro administrador" : "Este negocio no tiene administrador — crear uno"}
                            onClick={() => abrirCrearAdmin(n)}
                          >
                            <UserPlus size={15} />
                          </button>
                          <button className="btn-ghost !px-2" title="Aplicar suscripción demo" onClick={() => abrirDemo(n)}>
                            <Sparkles size={15} />
                          </button>
                          <button
                            className="btn-ghost !px-2"
                            title={`Agregar timbres extra (tiene ${n.timbresExtra} extra + ${n.timbresIncluidosMes}/mes)`}
                            onClick={() => abrirTimbres(n)}
                          >
                            <Ticket size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {negociosFiltrados.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-4 py-6 text-center text-slate-500">
                        {negocios.length === 0 ? "No hay negocios creados todavía." : "Ningún negocio coincide con tu búsqueda."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </>
            )}

            {tabActiva === "mapa" && (
            <>
            <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <MapPin size={18} /> Negocios en el mapa
            </h2>
            <NegociosMapa negocios={negocios} />
            </>
            )}
          </>
        )}
      </main>

      {/* Modal: nuevo negocio */}
      <Modal open={modalAbierto} onClose={() => setModalAbierto(false)} title="Nuevo negocio">
        <form onSubmit={crearNegocio} className="space-y-4">
          <div>
            <label className="label">Nombre del negocio</label>
            <input className="input" required value={form.nombreNegocio} onChange={(e) => setForm({ ...form, nombreNegocio: e.target.value })} />
          </div>
          <div>
            <label className="label">Subdominio</label>
            <div className="flex items-center gap-2">
              <input
                className="input"
                required
                pattern="[a-z0-9-]+"
                placeholder="miclinica"
                value={form.subdominio}
                onChange={(e) => setForm({ ...form, subdominio: e.target.value.toLowerCase() })}
              />
              <span className="text-sm text-slate-400 whitespace-nowrap">.mascotas.vet</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">Solo minúsculas, números y guiones.</p>
          </div>
          <div>
            <label className="label">Tipo de negocio</label>
            <select className="input" value={form.tipoNegocio} onChange={(e) => setForm({ ...form, tipoNegocio: e.target.value })}>
              {tiposNegocio.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label mb-2 block">Domicilio</label>
            <DomicilioCampos valores={domicilio} onChange={(campo, valor) => setDomicilio((d) => ({ ...d, [campo]: valor }))} />
            <p className="text-xs text-slate-400 mt-1">Se usa para ubicar el negocio en el mapa.</p>
          </div>
          <div>
            <label className="label">Teléfono</label>
            <input className="input" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
          </div>
          <hr className="border-slate-100" />
          <p className="text-xs font-medium text-slate-500">Cuenta de administrador del negocio</p>
          <div>
            <label className="label">Nombre del administrador</label>
            <input className="input" required value={form.nombreAdmin} onChange={(e) => setForm({ ...form, nombreAdmin: e.target.value })} />
          </div>
          <div>
            <label className="label">Correo</label>
            <input type="email" className="input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="label">Contraseña temporal</label>
            <input type="password" className="input" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          {errorForm && <p className="text-sm text-red-600">{errorForm}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setModalAbierto(false)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Creando..." : "Crear negocio"}</button>
          </div>
        </form>
      </Modal>

      {/* Modal: editar negocio existente */}
      <Modal open={!!negocioEditando} onClose={() => setNegocioEditando(null)} title={`Editar ${negocioEditando?.nombre ?? ""}`}>
        <form onSubmit={guardarEdicion} className="space-y-4">
          <div>
            <label className="label">Nombre del negocio</label>
            <input className="input" required value={formEditar.nombre} onChange={(e) => setFormEditar({ ...formEditar, nombre: e.target.value })} />
          </div>
          <div>
            <label className="label">Tipo de negocio</label>
            <select className="input" value={formEditar.tipoNegocio} onChange={(e) => setFormEditar({ ...formEditar, tipoNegocio: e.target.value })}>
              {tiposNegocio.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label mb-2 block">Domicilio</label>
            <DomicilioCampos valores={domicilioEditar} onChange={(campo, valor) => setDomicilioEditar((d) => ({ ...d, [campo]: valor }))} />
          </div>
          <div>
            <label className="label">Teléfono</label>
            <input className="input" value={formEditar.telefono} onChange={(e) => setFormEditar({ ...formEditar, telefono: e.target.value })} />
          </div>
          <div>
            <label className="label">Correo del negocio</label>
            <input type="email" className="input" value={formEditar.email} onChange={(e) => setFormEditar({ ...formEditar, email: e.target.value })} />
          </div>
          <hr className="border-slate-100" />
          <p className="text-xs font-medium text-slate-500">Administrador del negocio</p>
          <div>
            <label className="label">Nombre</label>
            <input className="input" value={formEditar.adminNombre} onChange={(e) => setFormEditar({ ...formEditar, adminNombre: e.target.value })} />
          </div>
          <div>
            <label className="label">Correo</label>
            <input type="email" className="input" value={formEditar.adminEmail} onChange={(e) => setFormEditar({ ...formEditar, adminEmail: e.target.value })} />
          </div>
          {errorEditar && <p className="text-sm text-red-600">{errorEditar}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setNegocioEditando(null)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={guardandoEditar}>{guardandoEditar ? "Guardando..." : "Guardar cambios"}</button>
          </div>
        </form>
      </Modal>

      {/* Modal: datos de acceso del negocio recién creado, listos para copiar y pegar */}
      <Modal
        open={!!datosAccesoNuevoNegocio}
        onClose={() => setDatosAccesoNuevoNegocio(null)}
        title="Negocio creado"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            <strong>{datosAccesoNuevoNegocio?.nombreNegocio}</strong> ya está listo. Esto es lo que pasó al intentar
            mandarle sus datos de acceso automáticamente:
          </p>

          <div className={`rounded-xl px-3 py-2 text-sm ${datosAccesoNuevoNegocio?.correoEnviado ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
            {datosAccesoNuevoNegocio?.correoEnviado ? (
              <>✅ Correo mandado a <strong>{datosAccesoNuevoNegocio?.email}</strong>.</>
            ) : (
              <>
                ❌ El correo a <strong>{datosAccesoNuevoNegocio?.email}</strong> NO se pudo mandar.
                {datosAccesoNuevoNegocio?.correoDetalle && <div className="mt-1 text-xs opacity-80">{datosAccesoNuevoNegocio.correoDetalle}</div>}
              </>
            )}
          </div>

          {datosAccesoNuevoNegocio?.telefono ? (
            <div className={`rounded-xl px-3 py-2 text-sm ${datosAccesoNuevoNegocio?.whatsappEnviado ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {datosAccesoNuevoNegocio?.whatsappEnviado ? (
                <>✅ WhatsApp mandado al <strong>{datosAccesoNuevoNegocio?.telefono}</strong>.</>
              ) : (
                <>
                  ❌ El WhatsApp al <strong>{datosAccesoNuevoNegocio?.telefono}</strong> NO se pudo mandar.
                  {datosAccesoNuevoNegocio?.whatsappDetalle && <div className="mt-1 text-xs opacity-80">{datosAccesoNuevoNegocio.whatsappDetalle}</div>}
                </>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400">Este negocio no tiene teléfono registrado, así que no se intentó por WhatsApp.</p>
          )}

          <p className="text-sm text-slate-600">
            De cualquier forma, aquí tienes el mensaje listo para copiar y mandarlo tú mismo por el canal que prefieras:
          </p>
          <pre className="whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-sans text-sm text-slate-700">
            {mensajeAccesoNuevoNegocio()}
          </pre>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setDatosAccesoNuevoNegocio(null)}>
              Cerrar
            </button>
            <button type="button" className="btn-primary" onClick={copiarDatosAccesoNuevoNegocio}>
              {copiadoAcceso ? (
                <>
                  <CheckCircle2 size={16} /> ¡Copiado!
                </>
              ) : (
                <>
                  <Copy size={16} /> Copiar mensaje
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: contraseña temporal generada */}
      <Modal open={!!passwordGenerada} onClose={() => setPasswordGenerada(null)} title="Contraseña restablecida">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Comparte esta contraseña temporal con el administrador de <strong>{passwordGenerada?.email}</strong> por un canal
            de confianza. No se volverá a mostrar.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-center font-mono text-lg tracking-wide">
            {passwordGenerada?.passwordTemporal}
          </div>
          <div className="flex justify-end">
            <button className="btn-primary" onClick={() => setPasswordGenerada(null)}>Listo</button>
          </div>
        </div>
      </Modal>

      {/* Modal: resultado de reenviar datos de acceso */}
      <Modal open={!!accesoReenviado} onClose={() => setAccesoReenviado(null)} title="Datos de acceso reenviados">
        <div className="space-y-3">
          <p className="text-sm text-slate-500">Se generó una contraseña nueva (la anterior deja de funcionar):</p>
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-center font-mono text-lg tracking-wide">
            {accesoReenviado?.passwordTemporal}
          </div>

          <div className={`rounded-xl px-3 py-2 text-sm ${accesoReenviado?.correoEnviado ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
            {accesoReenviado?.correoEnviado ? (
              <>✅ Correo mandado a <strong>{accesoReenviado?.email}</strong>.</>
            ) : (
              <>
                ❌ El correo a <strong>{accesoReenviado?.email}</strong> NO se pudo mandar.
                {accesoReenviado?.correoDetalle && <div className="mt-1 text-xs opacity-80">{accesoReenviado.correoDetalle}</div>}
              </>
            )}
          </div>

          {accesoReenviado?.telefono ? (
            <div className={`rounded-xl px-3 py-2 text-sm ${accesoReenviado?.whatsappEnviado ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {accesoReenviado?.whatsappEnviado ? (
                <>✅ WhatsApp mandado al <strong>{accesoReenviado?.telefono}</strong>.</>
              ) : (
                <>
                  ❌ El WhatsApp al <strong>{accesoReenviado?.telefono}</strong> NO se pudo mandar.
                  {accesoReenviado?.whatsappDetalle && <div className="mt-1 text-xs opacity-80">{accesoReenviado.whatsappDetalle}</div>}
                </>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400">Este negocio no tiene teléfono registrado, así que no se intentó por WhatsApp.</p>
          )}

          <div className="flex justify-end">
            <button className="btn-primary" onClick={() => setAccesoReenviado(null)}>Listo</button>
          </div>
        </div>
      </Modal>

      {/* Modal: aplicar suscripción demo */}
      <Modal open={!!negocioDemo} onClose={() => setNegocioDemo(null)} title={`Suscripción demo para ${negocioDemo?.nombre ?? ""}`}>
        <form onSubmit={aplicarDemo} className="space-y-4">
          <p className="text-sm text-slate-600">
            Activa el negocio con una suscripción de prueba (sin pago) por el número de días que elijas. Cuando termine,
            entrará al mismo período de gracia de 15 días que una suscripción de paga vencida.
          </p>
          <div>
            <label className="label">Días de prueba</label>
            <input
              type="number"
              className="input"
              required
              min={1}
              max={365}
              value={diasDemo}
              onChange={(e) => setDiasDemo(Number(e.target.value))}
            />
          </div>
          {negocioDemo?.suscripcion && (
            <p className="text-xs text-slate-400">
              Suscripción actual: {negocioDemo.suscripcion.plan} · {negocioDemo.suscripcion.estado} · vence el{" "}
              {new Date(negocioDemo.suscripcion.fechaVencimiento).toLocaleDateString("es-MX")}
            </p>
          )}
          {errorDemo && <p className="text-sm text-red-600">{errorDemo}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setNegocioDemo(null)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={aplicandoDemo}>
              {aplicandoDemo ? "Aplicando..." : "Aplicar demo"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal: agregar timbres extra a un negocio (límite de facturación) */}
      <Modal open={!!negocioTimbres} onClose={() => setNegocioTimbres(null)} title={`Timbres para ${negocioTimbres?.nombre ?? ""}`}>
        <form onSubmit={agregarTimbres} className="space-y-4">
          <p className="text-sm text-slate-600">
            Este negocio tiene {negocioTimbres?.timbresIncluidosMes ?? 0} timbres incluidos por mes
            {negocioTimbres && negocioTimbres.timbresExtra > 0 && <> + {negocioTimbres.timbresExtra} extra guardados</>}.
            Agrega aquí más timbres extra (no se resetean cada mes, se van descontando solo cuando ya se acabó la
            cuota mensual).
          </p>
          <div>
            <label className="label">Timbres a agregar</label>
            <input
              type="number"
              className="input"
              required
              value={cantidadTimbres}
              onChange={(e) => setCantidadTimbres(Number(e.target.value))}
            />
            <p className="mt-1 text-xs text-slate-400">
              Usa un número negativo si necesitas corregir una captura anterior.
            </p>
          </div>
          {errorTimbres && <p className="text-sm text-red-600">{errorTimbres}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setNegocioTimbres(null)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={agregandoTimbres || cantidadTimbres === 0}>
              {agregandoTimbres ? "Guardando..." : "Agregar timbres"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal: crear un administrador nuevo para un negocio — red de
          seguridad para cuando se quedó sin ninguno activo (o para agregarle
          un segundo administrador a cualquier negocio). */}
      <Modal
        open={!!negocioNuevoAdmin}
        onClose={() => setNegocioNuevoAdmin(null)}
        title={`Crear administrador para ${negocioNuevoAdmin?.nombre ?? ""}`}
      >
        <form onSubmit={crearAdministrador} className="space-y-4">
          {!negocioNuevoAdmin?.admin && (
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-700">
              Este negocio no tiene ningún administrador activo — nadie puede entrar a su panel hasta que crees uno.
            </p>
          )}
          <div>
            <label className="label">Nombre completo</label>
            <input
              className="input"
              required
              value={formNuevoAdmin.nombre}
              onChange={(e) => setFormNuevoAdmin({ ...formNuevoAdmin, nombre: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Correo</label>
            <input
              type="email"
              className="input"
              required
              value={formNuevoAdmin.email}
              onChange={(e) => setFormNuevoAdmin({ ...formNuevoAdmin, email: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Contraseña temporal</label>
            <input
              type="password"
              className="input"
              required
              minLength={6}
              value={formNuevoAdmin.password}
              onChange={(e) => setFormNuevoAdmin({ ...formNuevoAdmin, password: e.target.value })}
            />
          </div>
          {errorNuevoAdmin && <p className="text-sm text-red-600">{errorNuevoAdmin}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setNegocioNuevoAdmin(null)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={creandoAdmin}>
              {creandoAdmin ? "Creando..." : "Crear administrador"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal: resultado de crear el administrador — mismo patrón que
          "Negocio creado" y "Reenviar acceso": mostrar si de verdad se le
          avisó por correo/WhatsApp, no darlo por hecho. */}
      <Modal open={!!adminCreado} onClose={() => setAdminCreado(null)} title="Administrador creado">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            <strong>{adminCreado?.email}</strong> ya puede entrar al panel de <strong>{adminCreado?.nombreNegocio}</strong>.
            Esto es lo que pasó al intentar avisarle automáticamente:
          </p>

          <div className={`rounded-xl px-3 py-2 text-sm ${adminCreado?.correoEnviado ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
            {adminCreado?.correoEnviado ? (
              <>✅ Correo mandado a <strong>{adminCreado?.email}</strong>.</>
            ) : (
              <>
                ❌ El correo a <strong>{adminCreado?.email}</strong> NO se pudo mandar.
                {adminCreado?.correoDetalle && <div className="mt-1 text-xs opacity-80">{adminCreado.correoDetalle}</div>}
              </>
            )}
          </div>

          <div className={`rounded-xl px-3 py-2 text-sm ${adminCreado?.whatsappEnviado ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
            {adminCreado?.whatsappEnviado ? (
              <>✅ WhatsApp mandado.</>
            ) : (
              <>
                ❌ El WhatsApp NO se pudo mandar (o el negocio no tiene teléfono registrado).
                {adminCreado?.whatsappDetalle && <div className="mt-1 text-xs opacity-80">{adminCreado.whatsappDetalle}</div>}
              </>
            )}
          </div>

          <p className="text-sm text-slate-600">Por si acaso, guarda estos datos para compartirlos tú mismo:</p>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <p>Correo: <strong>{adminCreado?.email}</strong></p>
            <p>Contraseña: <strong>{adminCreado?.password}</strong></p>
          </div>
          <div className="flex justify-end">
            <button className="btn-primary" onClick={() => setAdminCreado(null)}>Listo</button>
          </div>
        </div>
      </Modal>

      {/* Modal: detalle de un negocio — todos sus clientes y mascotas, aun
          cuando alguna mascota ya haya sido dada de baja de ese negocio. */}
      <Modal
        open={!!negocioDetalle}
        onClose={() => setNegocioDetalle(null)}
        title={`${negocioDetalle?.nombre ?? ""} — clientes y mascotas`}
      >
        <div>
          <div className="mb-4 flex gap-2 border-b border-slate-100">
            <button
              className={`border-b-2 px-3 py-2 text-sm font-medium ${tabDetalle === "clientes" ? "border-brand-500 text-brand-600" : "border-transparent text-slate-500"}`}
              onClick={() => setTabDetalle("clientes")}
            >
              Clientes ({detalleClientes.length})
            </button>
            <button
              className={`border-b-2 px-3 py-2 text-sm font-medium ${tabDetalle === "mascotas" ? "border-brand-500 text-brand-600" : "border-transparent text-slate-500"}`}
              onClick={() => setTabDetalle("mascotas")}
            >
              Mascotas ({detalleMascotas.length})
            </button>
          </div>

          {cargandoDetalle ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : errorDetalle ? (
            <p className="text-sm text-red-600">{errorDetalle}</p>
          ) : tabDetalle === "clientes" ? (
            <div className="max-h-96 overflow-y-auto">
              {detalleClientes.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">Este negocio no tiene clientes todavía.</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                      <th className="py-2 pr-2 font-medium">Cliente</th>
                      <th className="py-2 pr-2 font-medium">Contacto</th>
                      <th className="py-2 pr-2 font-medium">Mascotas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalleClientes.map((c) => (
                      <tr key={c.id} className="border-b border-slate-50 last:border-0">
                        <td className="py-2 pr-2 text-slate-700">
                          {c.nombre}
                          {!c.activo && <span className="ml-1.5 text-xs text-slate-400">(inactivo)</span>}
                        </td>
                        <td className="py-2 pr-2 text-slate-500 text-xs">
                          {c.email || "—"}{c.telefono ? ` · ${c.telefono}` : ""}
                        </td>
                        <td className="py-2 pr-2 text-slate-700">{c.totalMascotas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {detalleMascotas.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">Este negocio no tiene mascotas todavía.</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                      <th className="py-2 pr-2 font-medium">Mascota</th>
                      <th className="py-2 pr-2 font-medium">Tutor</th>
                      <th className="py-2 pr-2 font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalleMascotas.map((m) => (
                      <tr key={m.id} className="border-b border-slate-50 last:border-0">
                        <td className="py-2 pr-2 text-slate-700">
                          {m.nombre} <span className="text-xs text-slate-400">({m.especie.toLowerCase()})</span>
                        </td>
                        <td className="py-2 pr-2 text-slate-500 text-xs">{m.clienteNombre}</td>
                        <td className="py-2 pr-2">
                          {m.eliminadaDelNegocio ? (
                            <span className="inline-flex items-center gap-1 text-xs text-red-500" title={m.motivoBajaDetalle ?? undefined}>
                              <UserX size={12} /> Baja · {ETIQUETAS_MOTIVO_BAJA[m.motivoBaja ?? ""] ?? m.motivoBaja}
                            </span>
                          ) : (
                            <Badge color={m.activo ? "verde" : "gris"}>{m.activo ? "Activa" : "Inactiva"}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="flex justify-end border-t border-slate-100 pt-4 mt-4">
            <button className="btn-secondary" onClick={() => setNegocioDetalle(null)}>
              <X size={14} /> Cerrar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
